import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Rendered as a count pill — unread notifications, open alerts. */
  badge?: number;
  end?: boolean;
};

export type NavGroup = {
  heading: string;
  items: NavItem[];
};

/**
 * The espresso rail. Grouped with headings rather than one flat list of 14
 * links — the admin nav in particular was unscannable as a single column.
 */
export function Sidebar({
  groups,
  brand,
  footer,
  open,
  onClose,
}: {
  groups: NavGroup[];
  brand: React.ReactNode;
  footer?: React.ReactNode;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {/* Scrim only exists below lg, where the rail is an overlay. */}
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-30 bg-void-950/50 backdrop-blur-[2px] transition-opacity lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <aside
        className={cn(
          "bg-void-950 text-void-200 fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col",
          "transition-transform duration-300 ease-(--ease-out-soft) lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="px-5 pt-5 pb-4">{brand}</div>

        <nav
          aria-label="Navigasi utama"
          className="scrollbar-slim flex-1 space-y-6 overflow-y-auto px-3 pb-4"
        >
          {groups.map((group) => (
            <div key={group.heading}>
              <p className="text-void-500 px-3 pb-1.5 text-2xs font-bold tracking-[0.12em] uppercase">
                {group.heading}
              </p>
              <ul className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon, badge, end }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      end={end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium",
                          "transition-colors duration-150",
                          isActive
                            ? "bg-accent text-accent-content font-bold shadow-accent"
                            : "text-void-300 hover:bg-void-800 hover:text-void-50",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            aria-hidden
                            className={cn("size-[18px] shrink-0", !isActive && "opacity-70")}
                          />
                          <span className="min-w-0 flex-1 truncate">{label}</span>
                          {badge != null && badge > 0 && (
                            <span
                              className={cn(
                                "tabular rounded-full px-1.5 py-0.5 text-2xs font-bold",
                                isActive
                                  ? "bg-void-950/15 text-void-950"
                                  : "bg-critical-500 text-white",
                              )}
                            >
                              {badge > 99 ? "99+" : badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {footer && <div className="border-void-800 border-t p-3">{footer}</div>}
      </aside>
    </>
  );
}

export function SidebarBrand({ subtitle }: { subtitle?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        aria-hidden
        className="bg-accent text-accent-content font-display grid size-9 place-items-center rounded-xl text-lg font-extrabold"
        style={{ borderRadius: "0.75rem 0.25rem 0.75rem 0.25rem" }}
      >
        M
      </span>
      <span className="min-w-0">
        <span className="font-display text-void-50 block text-lg leading-none font-extrabold tracking-tight">
          mediora
        </span>
        {subtitle && (
          <span className="text-void-500 block truncate text-2xs font-semibold tracking-wide uppercase">
            {subtitle}
          </span>
        )}
      </span>
    </div>
  );
}
